import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { Building2 } from "lucide-react";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const PaySlipHistory = () => {
  const [paySlips, setPaySlips] = useState([]);
  const [searchID, setSearchID] = useState("");
  const [filterMonthYear, setFilterMonthYear] = useState("");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");

  // Pagination states matching DepartmentMaster standard
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  useEffect(() => {
    const fetchPaySlips = async () => {
      try {
        const res = await axios.get("/api/payslips");
        if (res.data.success) {
          const allEmployees = res.data.data.flatMap(batch => 
            batch.employeePayslips.map(slip => ({
              ...slip,
              month: batch.month, 
              year: batch.year,   
              batchId: batch._id  
            }))
          );
          setPaySlips(allEmployees.filter((slip) => !isExEmployeeId(slip.employeeId)));
        }
      } catch (err) {
        toast.error("Fetch Error:", err);
      }
    };
    fetchPaySlips();
  }, []);

  const employeePrefixOptions = ["ALL", ...new Set(
    paySlips.map((slip) => getEmployeePrefix(slip.employeeId)).filter(Boolean)
  )];

  const filteredSlips = useMemo(() => {
    return paySlips.filter((slip) => {
      if (isExEmployeeId(slip.employeeId)) return false;
      const targetID = (slip.employeeUserId || "").toLowerCase();
      const searchTerm = searchID.toLowerCase();
      const matchesPrefix =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(slip.employeeId) === selectedEmployeePrefix;
      
      const matchesID = targetID.includes(searchTerm) || 
                         targetID.replace(/-/g, "").includes(searchTerm.replace(/-/g, ""));
      
      let matchesMonthYear = true;
      if (filterMonthYear) {
        const [y, m] = filterMonthYear.split("-");
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const selectedMonthName = monthNames[parseInt(m) - 1];
        
        matchesMonthYear = slip.month === selectedMonthName && slip.year.toString() === y;
      }

      return matchesID && matchesMonthYear && matchesPrefix;
    });
  }, [paySlips, searchID, filterMonthYear, selectedEmployeePrefix]);

  const paginatedSlips = useMemo(() => {
    if (pageSize === -1) return filteredSlips;
    const start = (currentPage - 1) * pageSize;
    return filteredSlips.slice(start, start + pageSize);
  }, [filteredSlips, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchID, filterMonthYear, selectedEmployeePrefix, pageSize]);

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section with Integrated Month & Status Filters */}
        <header className={masterHeader}>
          <div className={`${masterHeaderCard} flex-wrap gap-3`}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Payslip History</h1></div>
            </div>

            {/* Top Header Filters: Month & Status */}
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Month</label>
                <input 
                  type="month" 
                  className="p-1 border border-slate-300 rounded text-xs focus:border-blue-500 outline-none bg-white"
                  value={filterMonthYear}
                  onChange={(e) => setFilterMonthYear(e.target.value)}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-[10px] font-bold text-slate-500 uppercase mb-0.5">Status</label>
                <select
                  className="p-1 border border-slate-300 rounded text-xs focus:border-blue-500 outline-none bg-white min-w-[90px]"
                  value={selectedEmployeePrefix}
                  onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                >
                  {employeePrefixOptions.map((prefix) => (
                    <option key={prefix} value={prefix}>
                      {prefix}
                    </option>
                  ))}
                </select>
              </div>

              <div className="self-end">
                <BackButton />
              </div>
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
            
            <div className="border-b border-slate-200 flex-shrink-0 bg-white p-2 sm:p-3">
              <MobileHeaderToggle>
                {/* Mobile view compatibility wrapper */}
              </MobileHeaderToggle>

              {/* Integrated Master SearchBar & Pagination Controls */}
              <SearchBar 
                value={searchID} 
                onChange={(val) => {
                  setSearchID(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search by User ID..." 
                totalItems={filteredSlips.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

            {/* Table Area */}
            <div className="overflow-x-auto w-full">
              <table className={masterTable}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableData} style={{ width: '50px' }}>S.No</th>
                    <th className={masterTableData}>User ID</th>
                    <th className={masterTableData}>Name</th>
                    <th className={masterTableData}>Month</th>
                    <th className={masterTableData}>Year</th>
                    <th className={masterTableData}>Earnings</th>
                    <th className={masterTableData}>Deductions</th>
                    <th className={masterTableData}>Gross</th>
                    <th className={masterTableData}>OT Amt</th>
                    <th className={masterTableDatastatus}>Net Salary</th>
                  </tr>
                </thead>
                <tbody className={masterTbody}>
                  {paginatedSlips.length > 0 ? (
                    paginatedSlips.map((slip, index) => (
                      <tr key={`${slip.batchId}-${index}`} className={masterTableRow}>
                        <td className={masterTableCell}>
                          {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className={`${masterTableCell} text-blue-900 font-bold`}>{slip.employeeUserId || "-"}</td>
                        <td className={`${masterTableCell} text-left px-2 uppercase`}>{slip.employeeName}</td>
                        <td className={`${masterTableCell} font-bold`}>{slip.month}</td>
                        <td className={`${masterTableCell} font-bold`}>{slip.year}</td>
                        
                        <td className={`${masterTableCell} text-left`}>
                          {slip.earnings?.map((e, i) => (
                            <div key={i} className="flex justify-between border-b border-blue-100 last:border-0 text-xs">
                              <span className="text-gray-900">{e.headName}:</span> <span>₹{e.amount}</span>
                            </div>
                          ))}
                        </td>
                                     
                        <td className={`${masterTableCell} text-left`}>
                          {slip.deductions?.map((d, i) => (
                            <div key={i} className="flex justify-between border-b border-red-100 last:border-0 text-xs">
                              <span className="text-gray-900">{d.headName}:</span> <span>₹{d.amount}</span>
                            </div>
                          ))}
                        </td>

                        <td className={`${masterTableCell} text-blue-800 font-bold`}>₹{slip.grossSalary}</td>
                        <td className={`${masterTableCell} text-green-700`}>₹{slip.otAmount}</td>
                        <td className={`${masterTableDatastatus} bg-green-50 text-green-900 font-black`}>₹{slip.netSalary}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                        No records found in the Monthly Batch History.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default PaySlipHistory;